import {
  ButtonFieldProps,
  DynamicField,
  DynamicForm,
  FieldVariant,
  FileFieldProps,
  FileRepresentation,
  RadioFieldProps,
  SelectOption,
  TextareaFieldProps,
  TextFieldProps,
} from '@/components/form-builder/form-builder.types';
import { MeetingRepository } from '@/store/meeting-state/meeting-state.repository';
import { ResponseDoc } from '@/types';
import { FileRemoveEvent, FileSelectEvent } from 'primeng/fileupload';
import { map, of } from 'rxjs';

interface IGetPlanningReviewDetailsFormObjectProps {
  store: MeetingRepository;
  planningReviewDetails: any;
  comVerdictOptions: SelectOption[];
  index: number;
  openFile: (file: FileRepresentation) => void;
  download: (file: FileRepresentation) => void;
  isInspectMode: boolean;
  viewDetails?: () => void;
}

export const getPlanningReviewDetailsFormObject = ({
  planningReviewDetails,
  store,
  comVerdictOptions,
  index,
  openFile,
  download,
  isInspectMode,
  viewDetails,
}: IGetPlanningReviewDetailsFormObjectProps): DynamicForm => {
  const refField: DynamicField<TextFieldProps> = {
    label: 'meeting.planning.reviewDetails.form.ref',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: of(true),
    props: {
      value: of(planningReviewDetails.ref || ''),
    },
  };

  const acField: DynamicField<TextFieldProps> = {
    label: 'meeting.planning.reviewDetails.form.ac',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: of(true),
    props: {
      value: of(planningReviewDetails.ac?.nom || ''),
    },
  };

  const viewDetailsField: DynamicField<ButtonFieldProps> = {
    label: '',
    variant: FieldVariant.BUTTON,
    isRequired: false,
    isDisabled: of(false),
    props: {
      severity: 'secondary',
      class: 'w-full',
      label: 'meeting.planning.reviewDetails.form.viewDetails',
      onClick: () => {
        viewDetails?.();
      },
    },
  };

  const comObservationsField: DynamicField<TextareaFieldProps> = {
    label: '',
    variant: FieldVariant.TEXTAREA,
    isRequired: false,
    isDisabled: of(isInspectMode),
    props: {
      rows: 4,
      placeholder: 'meeting.planning.reviewDetails.form.comObservationsPlaceholder',
      value: store.getNestedObservable('plannedDossiers', index.toString(), 'observation'),
      onChange: (value: string) => {
        store.setNested('plannedDossiers', index.toString(), 'observation', value);
      },
    },
  };

  const comitteeVerdictField: DynamicField<RadioFieldProps> = {
    label: '',
    variant: FieldVariant.RADIO,
    isRequired: false,
    isDisabled: of(isInspectMode),
    props: {
      name: 'comitteeVerdict',
      options: comVerdictOptions,
      value: store
        .getNestedObservable<string>('plannedDossiers', index.toString(), 'decision')
        .pipe(map((decisionCode: string) => comVerdictOptions.find(opt => opt.code === decisionCode))),
      onChange: (value: SelectOption) => {
        store.setNested('plannedDossiers', index.toString(), 'decision', value.code);
      },
    },
  };

  const uploadField: DynamicField<FileFieldProps> = {
    label: 'meeting.planning.reviewDetails.form.files',
    variant: FieldVariant.FILE,
    isDisabled: of(isInspectMode),
    props: {
      mode: 'advanced',
      accept: 'image/*, application/pdf',
      multiple: true,
      value: store.getNestedObservable('plannedDossiers', index.toString(), 'files'),
      onFileSelect: (event: FileSelectEvent) => {
        store.setNested('plannedDossiers', index.toString(), 'files', [...event.currentFiles]);
      },
      onFileRemoved: (event: FileRemoveEvent) => {
        store.setNested(
          'plannedDossiers',
          index.toString(),
          'files',
          store.getNested<File[]>('plannedDossiers', index.toString(), 'files').filter(file => file.name !== event.file.name)
        );
      },
      files: store
        .getNestedObservable<ResponseDoc[]>('plannedDossiers', index.toString(), 'docs')
        .pipe(
          map(
            docs =>
              docs &&
              docs.map(doc => ({ id: doc.id, filename: doc.name, extension: doc.fileExtension, size: doc.fileSize, alfresco_id: doc.alfresco_id }))
          )
        ),
      onFileRepresentationChange: (File: FileRepresentation) => {
        store.setNested(
          'plannedDossiers',
          index.toString(),
          'docs',
          store.getNested<File[]>('plannedDossiers', index.toString(), 'docs').filter(file => file.name !== File.filename)
        );
      },
      openFile: (file: FileRepresentation) => openFile?.(file),
      onFileDownLoad: (file: FileRepresentation) => download?.(file),
      onAllRemoved: () => {
        store.setNested('plannedDossiers', index.toString(), 'docs', []);
      },
    },
  };

  return {
    title: 'meeting.planning.reviewDetails.form.title',
    description: '',
    orientation: 'horizontal',
    isHeaderHidden: true,
    grids: [
      {
        title: '',
        isHeaderHidden: true,
        gridItems: [
          {
            fields: [refField, acField],
          },
          {
            fields: [
              {
                label: '',
                variant: FieldVariant.EMPTY,
                props: { label: '' },
              },
              viewDetailsField,
            ],
          },
        ],
      },
      {
        title: 'meeting.planning.reviewDetails.form.comObservations',
        isHeaderHidden: false,
        gridItems: [
          {
            fields: [comObservationsField],
          },
        ],
      },
      {
        title: 'meeting.planning.reviewDetails.form.comitteeVerdict.label',
        isHeaderHidden: false,
        gridItems: [
          {
            fields: [comitteeVerdictField],
          },
        ],
      },
      {
        title: 'meeting.planning.reviewDetails.form.files',
        isHeaderHidden: false,
        gridItems: [
          {
            fields: [uploadField],
          },
        ],
      },
    ],
  };
};
